import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MouseEventHandler, TouchEventHandler } from "react";
import { BottomNav } from "./components/BottomNav";
import { IssueEditor } from "./components/IssueEditor";
import { StorageWarning } from "./components/StorageWarning";
import { VoiceNotesStatus, getVoiceQueueStorageState } from "./components/VoiceNotesStatus";
import { ExportRoute } from "./routes/ExportRoute";
import { ReviewRoute } from "./routes/ReviewRoute";
import { RoomIssuesRoute } from "./routes/RoomIssuesRoute";
import { RoomsRoute } from "./routes/RoomsRoute";
import { StartRoute } from "./routes/StartRoute";
import { CANONICAL_ORIGIN } from "./services/app/constants";
import { parseAppRoute, roomPath, type AppRoute } from "./services/app/routes";
import { countLocalReports, createReportDraft, getActiveReport, getReportSnapshot } from "./services/db/reportRepo";
import type { ReportSnapshot } from "./services/db/schema";
import { getStorageHealth, type StorageHealth } from "./services/app/storage";
import { getMediaStorageCapabilities } from "./services/db/mediaRepo";
import { trackDraftCreated, trackScreenView } from "./services/analytics/analyticsEvents";
import type { AnalyticsScreen } from "./services/analytics/analyticsTypes";
import { useTranscriptionQueue } from "./services/sync/transcriptionQueue";
import { ReportsRoute } from "./routes/ReportsRoute";
import { isMobileBuild, isNativeApp } from "./services/platform/runtime";
import { App as NativeApp } from "@capacitor/app";
import { APP_RESUMED_EVENT, NATIVE_BACK_EVENT } from "./services/platform/lifecycle";
import { NATIVE_CAPTURE_CHANGED } from "./services/platform/nativeCapture";
import { NativeCaptureRecoveryNotice } from "./components/NativeCaptureRecoveryNotice";
import { parentPath, swipeDestination } from "./services/app/navigation";

const AccountReportsRoute = lazy(() => import("./routes/AccountReportsRoute"));
const DeleteAccountRoute = lazy(() => import("./routes/DeleteAccountRoute"));
const InformationRoute = lazy(() => import("./routes/InformationRoute"));
const AdminRoute = lazy(() => import("./routes/AdminRoute"));

type MainNav = "rooms" | "review" | "export";
type SwipeDirection = "left" | "right";

interface NavigateOptions {
  scroll?: "top" | "restore";
}

export function App(): JSX.Element {
  useEffect(() => {
    if (!isNativeApp()) return;
    let stopped = false;
    let cleanup: (() => void) | undefined;
    void import("./services/commerce/storeBilling").then(({ startStoreRecovery }) => {
      if (!stopped) cleanup = startStoreRecovery();
    });
    return () => { stopped = true; cleanup?.(); };
  }, []);
  const [route, setRoute] = useState<AppRoute>(() => parseAppRoute(window.location.pathname));
  const [snapshot, setSnapshot] = useState<ReportSnapshot>();
  const [storageHealth, setStorageHealth] = useState<StorageHealth>();
  const [localReportCount, setLocalReportCount] = useState<number>();
  const [continueOnLegacyHost, setContinueOnLegacyHost] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string>();
  const scrollPositions = useRef<Record<string, number>>({});
  const previousScreenRef = useRef<AnalyticsScreen>();

  const refresh = useCallback(async () => {
    try {
      const [report, reportCount] = await Promise.all([getActiveReport(), countLocalReports()]);
      setLocalReportCount(reportCount);
      if (!report) {
        setSnapshot(undefined);
        setLoadError(undefined);
        setLoading(false);
        return;
      }
      const nextSnapshot = await getReportSnapshot(report.id);
      setSnapshot(nextSnapshot);
      setLoadError(undefined);
      setLoading(false);
    } catch {
      setSnapshot(undefined);
      setLocalReportCount(undefined);
      setLoadError("Local drafts are unavailable in this browser. Check browser storage permissions and try again.");
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    void getStorageHealth().then(setStorageHealth).catch(() => setStorageHealth(undefined));
    void getMediaStorageCapabilities().catch(() => undefined);
  }, [refresh]);

  useEffect(() => {
    const handlePopState = () => setRoute(parseAppRoute(window.location.pathname));
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const onResume = () => { void refresh(); };
    const onCapture = () => { void refresh(); };
    window.addEventListener(APP_RESUMED_EVENT, onResume);
    window.addEventListener(NATIVE_CAPTURE_CHANGED, onCapture);
    return () => {
      window.removeEventListener(APP_RESUMED_EVENT, onResume);
      window.removeEventListener(NATIVE_CAPTURE_CHANGED, onCapture);
    };
  }, [refresh]);

  useTranscriptionQueue(snapshot?.report.id, refresh);

  useEffect(() => {
    const screen = analyticsScreenForRoute(route);
    if (!screen) {
      return;
    }
    trackScreenView({
      screen,
      previousScreen: previousScreenRef.current,
      routePattern: routePattern(route)
    });
    previousScreenRef.current = screen;
  }, [route]);

  const report = snapshot?.report;
  const voiceQueueStorage = useMemo(
    () =>
      snapshot
        ? getVoiceQueueStorageState(snapshot.issues, snapshot.transcriptionJobs)
        : undefined,
    [snapshot]
  );
  const voiceRecordingDisabledReason = voiceQueueStorage?.hardLimit
    ? "Voice note storage is full. Type a note or let saved voice notes finish transcribing."
    : undefined;

  const currentNav = useMemo((): MainNav => {
    if (route.screen === "review") {
      return "review";
    }
    if (route.screen === "export") {
      return "export";
    }
    return "rooms";
  }, [route.screen]);

  const navigate = useCallback((path: string, options: NavigateOptions = {}): void => {
    if (document.querySelector('[data-recording-active="true"]')) return;
    const currentScrollKey = getScrollKey(route);
    if (currentScrollKey) {
      scrollPositions.current[currentScrollKey] = scrollContainer().scrollTop;
    }

    const nextRoute = parseAppRoute(path);
    const nextScrollKey = getScrollKey(nextRoute);
    const nextTop =
      options.scroll === "restore" && nextScrollKey ? scrollPositions.current[nextScrollKey] ?? 0 : 0;

    if (path !== window.location.pathname) {
      window.history.pushState({}, "", path);
      setRoute(nextRoute);
    }

    window.requestAnimationFrame(() => {
      scrollContainer().scrollTo({ top: nextTop, behavior: "auto" });
    });
  }, [route]);

  const swipeHandlers = useSwipeNavigation({
    enabled: (Boolean(report) || ["account", "delete-account", "reports"].includes(route.screen)) && route.screen !== "start" && route.screen !== "admin",
    navigate,
    route
  });

  useEffect(() => {
    if (!isNativeApp()) return;
    const listener = NativeApp.addListener("backButton", () => {
      if (document.querySelector('[role="dialog"], [aria-modal="true"]')) {
        window.dispatchEvent(new Event(NATIVE_BACK_EVENT));
        return;
      }
      const parent = parentPath(route);
      if (parent) navigate(parent, { scroll: "restore" });
      else void NativeApp.minimizeApp().catch(() => undefined);
    });
    return () => { void listener.then((handle) => handle.remove()).catch(() => undefined); };
  }, [navigate, route]);

  function navigateToTop(path: string): void {
    window.history.pushState({}, "", path);
    setRoute(parseAppRoute(path));
    window.requestAnimationFrame(() => scrollContainer().scrollTo({ top: 0, behavior: "auto" }));
  }

  async function startOrContinue(): Promise<void> {
    if (snapshot) {
      navigate("/rooms");
      return;
    }
    try {
      const nextSnapshot = await createReportDraft();
      setSnapshot(nextSnapshot);
      setLocalReportCount((count) => Math.max(1, count ?? 0));
      setLoadError(undefined);
      trackDraftCreated();
      navigateToTop("/rooms");
    } catch {
      setLoadError("Could not start a local draft. Check browser storage settings and try again.");
    }
  }

  if (loading) {
    return (
      <main className="app-shell">
        <section className="screen screen--center">
          <h1>MoveInReport</h1>
          <p className="helper">Opening your local draft...</p>
        </section>
      </main>
    );
  }

  if (isLegacyHost() && !continueOnLegacyHost) {
    return (
      <LegacyHostMigration
        hasLocalDrafts={(localReportCount ?? 0) > 0}
        onContinueHere={() => setContinueOnLegacyHost(true)}
      />
    );
  }

  let content: JSX.Element;
  if (route.screen === "information") {
    content = <Suspense fallback={<p>Loading...</p>}><InformationRoute page={route.page} /></Suspense>;
  } else if (route.screen === "delete-account") {
    content = <Suspense fallback={<p>Opening account settings...</p>}><DeleteAccountRoute /></Suspense>;
  } else if (route.screen === "account") {
    content = <Suspense fallback={<p>Opening saved reports...</p>}><AccountReportsRoute orderId={route.orderId} /></Suspense>;
  } else if (route.screen === "reports") {
    content = <ReportsRoute onOpen={async () => { await refresh(); navigate("/rooms"); }} />;
  } else if (route.screen === "admin" && !isMobileBuild()) {
    content = (
      <Suspense
        fallback={
          <section className="screen">
            <p className="helper">Loading admin...</p>
          </section>
        }
      >
        <AdminRoute />
      </Suspense>
    );
  } else if (!report || route.screen === "start" || route.screen === "admin") {
    content = (
      <StartRoute
        hasDraft={Boolean(report)}
        storageHealth={storageHealth}
        error={loadError}
        onStart={() => {
          void startOrContinue();
        }}
        onReports={() => navigate("/reports")}
      />
    );
  } else if (route.screen === "rooms") {
    content = (
      <RoomsRoute
        report={report}
        rooms={snapshot.rooms}
        issues={snapshot.issues}
        onNavigate={navigate}
        onChanged={refresh}
      />
    );
  } else if (route.screen === "room") {
    const room = snapshot.rooms.find((item) => item.id === route.roomId);
    content = room ? (
      <RoomIssuesRoute
        report={report}
        room={room}
        issues={snapshot.issues.filter((issue) => issue.roomId === room.id)}
        onNavigate={navigate}
        onChanged={refresh}
      />
    ) : (
      <MissingRoute onNavigate={navigate} />
    );
  } else if (route.screen === "issue") {
    const issue = snapshot.issues.find((item) => item.id === route.issueId);
    const room = snapshot.rooms.find((item) => item.id === route.roomId);
    const transcriptionJob = snapshot.transcriptionJobs.find(
      (job) => issue?.activeTranscriptionJobId && job.jobId === issue.activeTranscriptionJobId
    );
    content =
      issue && room ? (
        <IssueEditor
          key={issue.id}
          issue={issue}
          room={room}
          transcriptionJob={transcriptionJob}
          recordings={snapshot.recordings?.filter((recording) => recording.issueId === issue.id) ?? []}
          voiceRecordingDisabledReason={voiceRecordingDisabledReason}
          onBack={() => navigate(roomPath(room.id))}
          onChanged={refresh}
          onDeleted={() => {
            void refresh();
            navigate(roomPath(room.id));
          }}
        />
      ) : (
        <MissingRoute onNavigate={navigate} />
      );
  } else if (route.screen === "review") {
    content = (
      <ReviewRoute
        rooms={snapshot.rooms}
        issues={snapshot.issues}
        onNavigate={navigate}
        onChanged={refresh}
      />
    );
  } else {
    content = (
      <ExportRoute
        report={report}
        rooms={snapshot.rooms}
        issues={snapshot.issues}
        onChanged={refresh}
        onDeleted={() => {
          setSnapshot(undefined);
          navigate("/");
        }}
      />
    );
  }

  const showTabs = Boolean(report) && !["start", "admin", "reports", "account", "delete-account"].includes(route.screen);
  return (
    <main className={`app-shell ${route.screen === "start" ? "app-shell--start" : ""} ${isNativeApp() && showTabs ? "app-shell--native-tabs" : ""}`} {...swipeHandlers}>
      <div className="app-content">
        <NativeCaptureRecoveryNotice />
        {route.screen === "rooms" && (
          <div className="report-library-link">
            <button className="button" type="button" onClick={() => navigate("/reports")}>Your reports</button>
          </div>
        )}
        {content}
        {snapshot && showTabs && (
          <>
            {storageHealth?.lowStorage && (
              <section className="screen" aria-label="Storage note">
                <StorageWarning health={storageHealth} />
              </section>
            )}
            {voiceQueueStorage?.warning && !voiceQueueStorage.hardLimit && (
              <section className="screen" aria-label="Voice note storage note">
                <div className="notice">
                  You have many saved voice notes. They are safe on this device, but transcription may take a few minutes when online.
                </div>
              </section>
            )}
            <VoiceNotesStatus
              rooms={snapshot.rooms}
              issues={snapshot.issues}
              jobs={snapshot.transcriptionJobs}
              onNavigate={navigate}
              onChanged={refresh}
            />
          </>
        )}
      </div>
      {showTabs && <BottomNav current={currentNav} onNavigate={navigate} />}
    </main>
  );
}

function isLegacyHost(): boolean {
  if (isMobileBuild()) return false;
  return window.location.hostname === "moveinreport-prod.web.app" || window.location.hostname === "moveinreport-prod.firebaseapp.com";
}

function scrollContainer(): Element {
  return document.querySelector(".app-shell--native-tabs > .app-content") ?? document.scrollingElement ?? document.documentElement;
}

function analyticsScreenForRoute(route: AppRoute): AnalyticsScreen | undefined {
  switch (route.screen) {
    case "start":
      return "start";
    case "rooms":
      return "rooms";
    case "room":
      return "room_detail";
    case "issue":
      return "condition_note_detail";
    case "review":
      return "review";
    case "export":
      return "export";
    case "admin":
    case "reports":
    case "account":
    case "delete-account":
    case "information":
      return undefined;
  }
}

function routePattern(route: AppRoute): string {
  switch (route.screen) {
    case "information":
      return `/${route.page}`;
    case "delete-account":
      return "/account/delete";
    case "account":
      return route.orderId ? "/account/reports/:orderId" : "/account/reports";
    case "reports":
      return "/reports";
    case "start":
      return "/";
    case "rooms":
      return "/rooms";
    case "room":
      return "/rooms/:roomId";
    case "issue":
      return "/rooms/:roomId/condition-notes/:conditionNoteId";
    case "review":
      return "/review";
    case "export":
      return "/export";
    case "admin":
      return "/admin";
  }
}

function LegacyHostMigration({
  hasLocalDrafts,
  onContinueHere
}: {
  hasLocalDrafts: boolean;
  onContinueHere: () => void;
}): JSX.Element {
  useEffect(() => {
    if (hasLocalDrafts) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      window.location.replace(CANONICAL_ORIGIN);
    }, 1600);
    return () => window.clearTimeout(timer);
  }, [hasLocalDrafts]);

  return (
    <main className="app-shell app-shell--start">
      <section className="screen screen--center start-screen">
        <h1 className="start-title">MoveInReport has moved</h1>
        {hasLocalDrafts ? (
          <>
            <p className="start-copy">This draft is saved on this older site address.</p>
            <p className="trust-line">
              Browser privacy rules keep local drafts separate by website address. Photos stay on this device.
            </p>
            <div className="button-row">
              <button className="button button--primary" type="button" onClick={onContinueHere}>
                Finish/export this draft here
              </button>
              <a className="button" href={CANONICAL_ORIGIN}>
                Start fresh on moveinreport.com
              </a>
            </div>
          </>
        ) : (
          <>
            <p className="start-copy">Taking you to moveinreport.com.</p>
            <a className="button button--primary" href={CANONICAL_ORIGIN}>
              Open moveinreport.com
            </a>
          </>
        )}
      </section>
    </main>
  );
}

function MissingRoute({ onNavigate }: { onNavigate: (path: string) => void }): JSX.Element {
  return (
    <section className="screen">
      <div className="notice notice--warning">That local item is no longer available.</div>
      <button className="button button--primary" type="button" onClick={() => onNavigate("/rooms")}>
        Back to rooms
      </button>
    </section>
  );
}

function useSwipeNavigation({
  enabled,
  navigate,
  route
}: {
  enabled: boolean;
  navigate: (path: string, options?: NavigateOptions) => void;
  route: AppRoute;
}): {
  onClickCapture: MouseEventHandler<HTMLElement>;
  onTouchCancel: TouchEventHandler<HTMLElement>;
  onTouchEnd: TouchEventHandler<HTMLElement>;
  onTouchMove: TouchEventHandler<HTMLElement>;
  onTouchStart: TouchEventHandler<HTMLElement>;
} {
  const gesture = useRef<{ startX: number; startY: number; lastX: number; lastY: number } | undefined>(
    undefined
  );
  const suppressClickUntil = useRef(0);

  const onTouchStart: TouchEventHandler<HTMLElement> = (event) => {
    if (!enabled || event.touches.length !== 1 || shouldIgnoreSwipeTarget(event.target) || document.querySelector('[role="dialog"]')) {
      gesture.current = undefined;
      return;
    }

    const touch = event.touches[0];
    gesture.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      lastX: touch.clientX,
      lastY: touch.clientY
    };
  };

  const onTouchMove: TouchEventHandler<HTMLElement> = (event) => {
    if (!gesture.current || event.touches.length !== 1) {
      return;
    }

    const touch = event.touches[0];
    gesture.current.lastX = touch.clientX;
    gesture.current.lastY = touch.clientY;
  };

  const onTouchEnd: TouchEventHandler<HTMLElement> = (event) => {
    if (!gesture.current) {
      return;
    }

    const finishedGesture = gesture.current;
    gesture.current = undefined;
    const touch = event.changedTouches[0];
    const endX = touch?.clientX ?? finishedGesture.lastX;
    const endY = touch?.clientY ?? finishedGesture.lastY;
    const deltaX = endX - finishedGesture.startX;
    const deltaY = endY - finishedGesture.startY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (absX < 72 || absX < absY * 1.35 || absY > 96) {
      return;
    }

    const direction: SwipeDirection = deltaX < 0 ? "left" : "right";
    const destination = swipeDestination(direction, route);

    if (!destination) {
      return;
    }

    suppressClickUntil.current = Date.now() + 450;
    navigate(destination, { scroll: "restore" });
  };

  const onTouchCancel: TouchEventHandler<HTMLElement> = () => {
    gesture.current = undefined;
  };

  const onClickCapture: MouseEventHandler<HTMLElement> = (event) => {
    if (Date.now() > suppressClickUntil.current) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  };

  return {
    onClickCapture,
    onTouchCancel,
    onTouchEnd,
    onTouchMove,
    onTouchStart
  };
}

function getScrollKey(route: AppRoute): string | undefined {
  if (route.screen === "rooms" || route.screen === "review" || route.screen === "export") {
    return route.screen;
  }
  if (route.screen === "room") {
    return `room:${route.roomId}`;
  }
  if (route.screen === "issue") {
    return `issue:${route.issueId}`;
  }
  return undefined;
}

function shouldIgnoreSwipeTarget(target: EventTarget): boolean {
  return target instanceof Element
    ? Boolean(
        target.closest(
          [
            "a",
            "input",
            "textarea",
            "select",
            "label",
            "summary",
            "[contenteditable='true']",
            "[data-swipe-ignore='true']",
            ".bottom-nav",
            ".dialog",
            ".dialog-backdrop",
            ".fab",
            ".recorder"
          ].join(",")
        )
      )
    : true;
}
